import * as React from "react"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet"

/** Portaled UI opened FROM inside a drawer — outside clicks on these must not dismiss the drawer. */
const NESTED_PORTAL_SELECTOR = [
  "[data-radix-popper-content-wrapper]",
  "[data-radix-select-content]",
  "[data-radix-menu-content]",
  "[data-radix-dropdown-menu-content]",
  "[data-radix-popover-content]",
  "[role='listbox']",
  "[data-radix-dialog-content]:not([data-floating-drawer-panel])",
].join(",")

function isNestedPortaledUi(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return !!target.closest(NESTED_PORTAL_SELECTOR)
}

/**
 * True while a nested Popover / Select / Menu (etc.) is mounted.
 * Used so the FIRST outside click closes only that layer; the drawer stays
 * open. A second click on the scrim then closes the drawer.
 */
function hasOpenNestedPortaledUi(): boolean {
  if (typeof document === "undefined") return false
  return !!document.querySelector(NESTED_PORTAL_SELECTOR)
}

/**
 * How long to keep ignoring drawer-close requests after a pointerdown that saw
 * an open nested layer. Modal Popovers unmount in the same event turn when
 * dismissed, so Dialog's onOpenChange(false) can fire after
 * `hasOpenNestedPortaledUi()` is already false — without this grace the first
 * outside click would close BOTH the dropdown and the drawer.
 *
 * IMPORTANT: we must NOT `preventDefault()` on the Dialog's
 * onPointerDownOutside / onInteractOutside while a nested layer is open.
 * Radix shares one dismissable-layer branch — parent preventDefault also
 * blocks the nested Popover from closing (Stage Settings template picker
 * stayed stuck open).
 */
const NESTED_DISMISS_GRACE_MS = 400

/**
 * File inputs inside a FloatingDrawer must cover their visible hit target.
 * NEVER use Tailwind `sr-only` / `hidden`+label focus here: focusing a
 * clipped/off-screen control makes the browser `scrollIntoView` it, which
 * scrolls the document under Radix's scroll-lock and leaves the fixed panel
 * visually shifted up until a full page refresh.
 */
export const floatingDrawerFileInputCls =
  "absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"

/**
 * FloatingDrawer — the canonical V3 side drawer.
 *
 * A thin composite over the shadcn `Sheet` primitive using its `floating`
 * variant: a rounded panel inset 12px from the viewport edge over a soft
 * blurred scrim (see the V3 design-system "Floating drawer" tab). It is NOT a
 * new drawer primitive — it reuses Radix Dialog under the hood, so focus-trap,
 * scroll-lock, Esc-to-close, overlay-click-to-close, portalling and ARIA all
 * come for free. Use this everywhere a right-rail / detail side panel is needed
 * so every drawer in the app looks and behaves identically.
 *
 * Layout contract: pass `FloatingDrawerHeader`, then `FloatingDrawerBody`
 * (the single scroll region), then optionally `FloatingDrawerFooter`.
 *
 * Accessibility: `FloatingDrawerHeader` renders the required Radix dialog title.
 * If you render a drawer WITHOUT a `FloatingDrawerHeader`, pass `title` here so
 * the dialog still has an accessible name (rendered visually hidden).
 */
export interface FloatingDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Which edge the drawer docks to. Default "right". */
  side?: "left" | "right"
  /** Panel width in px (capped to viewport − 24px on small screens). Default 480. */
  width?: number
  /** Accessible name fallback when no FloatingDrawerHeader is used. */
  title?: string
  /** Extra classes on the panel (e.g. a max-width). */
  className?: string
  children: React.ReactNode
  /**
   * Optional control (e.g. a drag-to-resize grip) rendered as a SIBLING of
   * the content, outside its rounded-corner clip. The panel itself carries
   * no `overflow-hidden` of its own — see the comment above the inner
   * wrapper below — so anything passed as a plain `children` node that
   * positions itself outside the panel's box (e.g. `-left-4`) would
   * otherwise render, unclipped, past the drawer's edge over the scrim.
   * Passing it here instead keeps that intentional "floating over the
   * backdrop" look working everywhere, not just half-visible.
   */
  resizeHandle?: React.ReactNode
}

export function FloatingDrawer({
  open,
  onOpenChange,
  side = "right",
  width = 480,
  title,
  className,
  children,
  resizeHandle,
}: FloatingDrawerProps) {
  // Set in capture-phase pointerdown when a nested portaled layer is open;
  // read when Dialog asks to close AFTER the nested layer has unmounted.
  const nestedDismissGraceUntilRef = React.useRef(0)

  const markNestedDismissGrace = React.useCallback(() => {
    nestedDismissGraceUntilRef.current = performance.now() + NESTED_DISMISS_GRACE_MS
  }, [])

  const isInNestedDismissGrace = React.useCallback(
    () => performance.now() < nestedDismissGraceUntilRef.current,
    []
  )

  /**
   * Controlled open gate: ignore close requests that belong to a nested
   * Popover/Select dismiss gesture. Do not use preventDefault on Dialog
   * outside-events for that case — it blocks the nested layer from closing.
   */
  const requestOpenChange = React.useCallback(
    (next: boolean) => {
      if (
        !next &&
        (isInNestedDismissGrace() || hasOpenNestedPortaledUi())
      ) {
        return
      }
      onOpenChange(next)
    },
    [onOpenChange, isInNestedDismissGrace]
  )

  // Belt-and-suspenders dismiss: nested Popovers/Selects (and legacy
  // stopPropagation on preview popovers) can swallow Radix interact-outside,
  // so we also close when the user pointerdowns the scrim itself — BUT only
  // when no nested portaled layer is open. Otherwise the first outside click
  // must dismiss the dropdown/select only; a second click closes the drawer.
  React.useEffect(() => {
    if (!open) {
      nestedDismissGraceUntilRef.current = 0
      return
    }

    const onPointerDownCapture = (e: PointerEvent) => {
      const target = e.target
      if (!(target instanceof Element)) return

      // Stamp grace as soon as we see an open nested layer on THIS gesture,
      // before Radix tears the popover down and Dialog asks to close.
      if (isNestedPortaledUi(target) || hasOpenNestedPortaledUi()) {
        markNestedDismissGrace()
      }

      if (!target.closest("[data-floating-drawer-overlay]")) return

      // Nested dropdown/select/menu is (or was, this gesture) open — let the
      // event reach Radix so THAT layer can dismiss. Drawer close is suppressed
      // via requestOpenChange + grace (do not preventDefault/stopPropagation).
      if (
        isInNestedDismissGrace() ||
        isNestedPortaledUi(target) ||
        hasOpenNestedPortaledUi()
      ) {
        return
      }

      // Prevent the click from falling through to the board (dnd / cards)
      // after the scrim unmounts.
      e.preventDefault()
      e.stopPropagation()
      requestOpenChange(false)
    }

    document.addEventListener("pointerdown", onPointerDownCapture, true)
    return () => document.removeEventListener("pointerdown", onPointerDownCapture, true)
  }, [open, requestOpenChange, markNestedDismissGrace, isInNestedDismissGrace])

  // OS file pickers steal window focus. On return, Chromium may have scrolled
  // `document` under the scroll-lock (esp. when a clipped file input was
  // focused). Snap the document back so the fixed panel is not left shifted.
  React.useEffect(() => {
    if (!open) return

    const resetDocumentScroll = () => {
      window.scrollTo(0, 0)
      const root = document.scrollingElement
      if (root) root.scrollTop = 0
      if (document.body.scrollTop) document.body.scrollTop = 0
    }

    const onWindowFocus = () => {
      resetDocumentScroll()
      // One more frame — RemoveScroll may re-apply offsets after focus returns.
      requestAnimationFrame(resetDocumentScroll)
    }

    window.addEventListener("focus", onWindowFocus)
    return () => window.removeEventListener("focus", onWindowFocus)
  }, [open])

  return (
    <Sheet open={open} onOpenChange={requestOpenChange}>
      <SheetContent
        floating
        side={side}
        hideClose
        className={cn("p-0 gap-0", className)}
        style={{ width }}
        onPointerDownOutside={(e) => {
          // Click landed ON nested portaled content — keep drawer open.
          if (isNestedPortaledUi(e.target)) {
            e.preventDefault()
            return
          }
          // Nested layer open (or just dismissed this gesture): allow the event
          // through so the Popover/Select can close. Swallow drawer close via
          // requestOpenChange — do NOT preventDefault (Radix shared branch).
          if (hasOpenNestedPortaledUi() || isInNestedDismissGrace()) {
            markNestedDismissGrace()
            return
          }
          requestOpenChange(false)
        }}
        onInteractOutside={(e) => {
          if (isNestedPortaledUi(e.target)) {
            e.preventDefault()
            return
          }
          if (hasOpenNestedPortaledUi() || isInNestedDismissGrace()) {
            markNestedDismissGrace()
            // no preventDefault — nested Popover must receive a clean dismiss
          }
        }}
        onFocusOutside={(e) => {
          // Modal Popover/Select steals focus; don't treat that as drawer dismiss.
          if (
            isInNestedDismissGrace() ||
            isNestedPortaledUi(e.target) ||
            hasOpenNestedPortaledUi()
          ) {
            e.preventDefault()
            return
          }
          // OS file pickers steal focus with no focusable relatedTarget. Without
          // this, Upload logo / document inputs close (or half-close) the drawer
          // the moment the native picker opens — leaving an empty broken panel.
          // Stamp grace so a late onOpenChange(false) from the same gesture is
          // also swallowed by requestOpenChange.
          const oe = e.detail?.originalEvent
          if (oe instanceof FocusEvent && !oe.relatedTarget) {
            markNestedDismissGrace()
            e.preventDefault()
          }
        }}
        onEscapeKeyDown={(e) => {
          // Esc dismisses the topmost layer only. preventDefault here is safe
          // (unlike pointer-outside) — it only blocks THIS dialog from closing;
          // the nested Popover still receives Esc via its own layer.
          if (hasOpenNestedPortaledUi()) {
            markNestedDismissGrace()
            e.preventDefault()
          }
        }}
      >
        {/* a11y fallback: guarantees the dialog always has an accessible name
            even if the consumer doesn't render a FloatingDrawerHeader. */}
        {title ? (
          <>
            <SheetTitle className="sr-only">{title}</SheetTitle>
            <SheetDescription className="sr-only">{title}</SheetDescription>
          </>
        ) : null}
        {/* `resizeHandle` is a SIBLING of the clipped wrapper below, not
            inside it — the panel itself no longer clips (see sheet.tsx), so
            this is the only thing allowed to render past the panel's edge. */}
        {resizeHandle}
        {/* Content clipping moved here (off the panel itself) so a
            `resizeHandle` can float outside the rounded corners while
            everything else still gets clipped to them exactly as before —
            including drawers with no FloatingDrawerFooter, where the body
            would otherwise show square corners at the bottom. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Drawer header — title (+ optional subtitle / leading icon / trailing
 * actions) and a wired close button. The title IS the Radix dialog title.
 */
export function FloatingDrawerHeader({
  title,
  subtitle,
  icon,
  right,
  className,
}: {
  title: React.ReactNode
  subtitle?: React.ReactNode
  icon?: React.ReactNode
  right?: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        "z-10 flex shrink-0 items-center gap-3 rounded-t-2xl border-b border-[#EAECF0] bg-white/95 px-5 py-4 backdrop-blur",
        className
      )}
    >
      {icon ? (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#F2F4F7] text-[#475467]">
          {icon}
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <SheetTitle className="truncate text-[15px] font-semibold text-[#101828]">
          {title}
        </SheetTitle>
        {subtitle ? (
          <div className="flex min-w-0 items-center gap-1.5 text-[12px] text-[#6B7280]">
            {subtitle}
          </div>
        ) : null}
      </div>
      {right}
      <SheetClose
        className="flex h-8 w-8 items-center justify-center rounded-lg text-[#475467] transition-colors hover:bg-[#F2F4F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0EB64F]/40"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </SheetClose>
    </div>
  )
}

/** The single scroll region of the drawer. */
export function FloatingDrawerBody({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn("min-h-0 flex-1 overflow-y-auto px-5 py-4", className)}>
      {children}
    </div>
  )
}

/** Action footer, pinned to the bottom of the drawer as a flex sibling. */
export function FloatingDrawerFooter({
  className,
  children,
}: {
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        // shrink-0 flex sibling — NOT sticky. Sticky floats mid-panel when any
        // ancestor becomes a scrollport (file-input scrollIntoView / overflow
        // quirks), leaving a blank void under the footer until refresh.
        "z-10 flex shrink-0 items-center justify-end gap-2 rounded-b-2xl border-t border-[#EAECF0] bg-white/95 px-5 py-3 backdrop-blur",
        className
      )}
    >
      {children}
    </div>
  )
}
